import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Apple, Play, Instagram, Twitter, Mail } from "lucide-react";
import { fadeUp, viewportOnce } from "@/lib/motion";
import { APP_STORE_URL, PLAY_STORE_URL, goExternal, goCustomer } from "@/lib/links";
import { CONTACT_EMAIL, CONTACT_MAILTO } from "@/lib/contact";
import { useBusinessCta } from "@/lib/useBusinessCta";
import { Logo } from "@/components/landing/Logo";

// Every footer link has either a `path` (React Router), `scroll` target
// (in-page section id) or `href` (external / mailto). No more dead `#` links.
const COLS = [
  {
    title: "Company",
    links: [
      { label: "About", scroll: "#about" },
      { label: "Foundation", scroll: "#foundation" },
      { label: "Look Book", scroll: "#gallery" },
      { label: "Contact", href: CONTACT_MAILTO },
    ],
  },
  {
    title: "Product",
    links: [
      { label: "For Business", action: "business" },
      { label: "For Customers", action: "customer" },
      { label: "Pricing", path: "/pricing", testid: "footer-pricing-link" },
    ],
  },
];

export default function Footer() {
  const navigate = useNavigate();
  const { onClick: handleBusinessCta } = useBusinessCta();

  const handleLink = (link) => (e) => {
    e.preventDefault();
    if (link.path) {
      navigate(link.path);
      return;
    }
    if (link.scroll) {
      const el = document.querySelector(link.scroll);
      if (el) el.scrollIntoView({ behavior: "smooth" });
      return;
    }
    if (link.action === "business") return handleBusinessCta();
    if (link.action === "customer") return goCustomer();
    if (link.href) window.location.href = link.href;
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative z-10 border-t border-white/10 bg-[#2A2724] px-6 pt-24 pb-10 md:px-12"
    >
      <div className="mx-auto max-w-[1600px]">
        {/* CTA band */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="flex flex-col items-start justify-between gap-10 border-b border-white/10 pb-20 lg:flex-row lg:items-end"
        >
          <h2 className="font-serif text-5xl md:text-7xl lg:text-8xl font-light leading-[0.9] tracking-tighter text-[#F7F5F2]">
            Get Booked In,
            <br />
            <span className="italic text-gold">last minute.</span>
          </h2>
          <div className="flex flex-wrap gap-3">
            <button
              data-testid="footer-download-ios"
              onClick={() => goExternal(APP_STORE_URL)}
              className="group inline-flex items-center gap-3 rounded-full bg-gold px-7 py-4 text-black transition-colors duration-300 hover:bg-[#AA8627]"
            >
              <Apple className="h-5 w-5" />
              <span className="text-sm font-medium">App Store</span>
            </button>
            <button
              data-testid="footer-download-android"
              onClick={() => goExternal(PLAY_STORE_URL)}
              className="inline-flex items-center gap-3 rounded-full border border-white/20 px-7 py-4 text-[#F7F5F2] transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              <Play className="h-4 w-4" />
              <span className="text-sm font-medium">Google Play</span>
            </button>
          </div>
        </motion.div>

        {/* Links */}
        <div className="grid grid-cols-2 gap-10 py-16 md:grid-cols-4">
          <div className="col-span-2 md:col-span-2">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-3"
            >
              <Logo size={54} className="shrink-0" />
              <span className="font-serif text-3xl font-semibold tracking-tight text-[#F7F5F2]">
                Book Away
              </span>
            </button>
            <p className="mt-4 max-w-xs text-sm text-[#A79F94]">
              The last-minute marketplace for beauty & grooming. Fill the chair,
              book the look.
            </p>
            <a
              data-testid="footer-contact-email"
              href={CONTACT_MAILTO}
              className="mt-4 inline-flex items-center gap-2 text-sm text-[#F7F5F2] transition-colors duration-300 hover:text-gold"
            >
              <Mail className="h-4 w-4 text-gold" />
              {CONTACT_EMAIL}
            </a>
            <div className="mt-6 flex gap-3">
              <a
                data-testid="social-instagram"
                href="#"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-[#A79F94] transition-colors duration-300 hover:border-gold hover:text-gold"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                data-testid="social-twitter"
                href="#"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-[#A79F94] transition-colors duration-300 hover:border-gold hover:text-gold"
              >
                <Twitter className="h-4 w-4" />
              </a>
            </div>
          </div>
          {COLS.map((col) => (
            <div key={col.title}>
              <p className="overline mb-5">{col.title}</p>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <a
                      href={l.path || l.href || l.scroll || "#"}
                      onClick={handleLink(l)}
                      data-testid={l.testid}
                      className="text-sm text-[#A79F94] transition-colors duration-300 hover:text-[#F7F5F2] cursor-pointer"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 text-xs text-[#8f857a] md:flex-row md:items-center">
          <p>© {new Date().getFullYear()} Book Away. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <a
              data-testid="footer-terms-link"
              href="/terms"
              className="transition-colors duration-300 hover:text-gold"
            >
              Terms
            </a>
            <a
              data-testid="footer-privacy-link"
              href="/privacy"
              className="transition-colors duration-300 hover:text-gold"
            >
              Privacy
            </a>
            <a
              data-testid="footer-support-link"
              href={CONTACT_MAILTO}
              className="transition-colors duration-300 hover:text-gold"
            >
              Support
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
